import { isDefined } from 'common/TypeUtilities';
import { map } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useCartFoodItemsLazyQuery } from 'src/gqlReactTypings.generated.d';
import { useDispatch, useSelector } from 'src/store';
import { updateFoodItems } from 'src/store/cart/actions';
import { selectFullLineItems } from 'src/store/cart/selectors';
import { CartFoodItem } from 'src/store/cart/types';

/**
 * Refresh data for food items currently stored in the cart in order
 * to reduce the amount of errors due to prices changing. Consider removing this when
 * carts are all moved to server.
 */
export const useReloadCartFoodItems = () => {
  const dispatch = useDispatch();
  const lineItems = useSelector((state) => selectFullLineItems(state.cart));
  const foodItemIds = lineItems.map((lineItem) => lineItem.foodItemId).filter((id) => id);
  const [runQuery, { data, loading, error }] = useCartFoodItemsLazyQuery({
    variables: {
      foodItemIds,
    },
  });

  // Fetch latest food item data from server.
  useEffect(() => {
    if (loading || data || error || !foodItemIds.length) {
      return;
    }

    runQuery({
      variables: {
        foodItemIds,
      },
    });
  }, [runQuery, loading, data, error, foodItemIds]);

  // Check if there's a price change.
  const [latestFoodItemById, requiresUpdate] = useMemo(() => {
    if (data) {
      const latestFoodItemById: Record<number, CartFoodItem> = data.foodItems.reduce((obj, foodItem) => {
        obj[foodItem.id] = foodItem;
        return obj;
      }, {});

      const requiresUpdate = lineItems.find((lineItem) => {
        const { foodItemId } = lineItem;
        // This shouldn't really happen, but just in case let's skip.
        if (!latestFoodItemById[foodItemId]) {
          return false;
        }

        return latestFoodItemById[foodItemId].price !== lineItem.foodItem.price;
      });

      return [latestFoodItemById, Boolean(requiresUpdate)];
    }
    return [{}, false];
  }, [data, lineItems]);

  // Update line items if needed.
  useEffect(() => {
    if (requiresUpdate) {
      const foodItemsForLineItems = map(lineItems, 'foodItem.id')
        .map((id) => latestFoodItemById[id])
        .filter(isDefined);
      dispatch(updateFoodItems({ foodItems: foodItemsForLineItems }));
    }
  }, [dispatch, lineItems, latestFoodItemById, requiresUpdate]);
};
